import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import type { User } from '@recruitee/account-types';
import type { Attachment } from '@recruitee/attachments-types';
import { trackByProp } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { EditorButton } from '@recruitee/prosemirror';
import { EditorJSONState } from '@recruitee/prosemirror-types';
import {
  ProsemirrorViewboxConfig,
  ProsemirrorViewboxConfigDirective,
  WysiwygComponent,
} from '@recruitee/prosemirror/webapp';
import { ApprovalRequestPayload } from '@recruitee/requisitions-data';
import {
  NotificationsController,
  DialogBodyComponent,
  HeadingComponent,
  AdminsAvatarsComponent,
  AttachmentItemComponent,
  IconComponent,
  DialogHeaderComponent,
  ButtonComponent,
  DialogFooterComponent,
  BadgeComponent,
  DividerComponent,
  SpinnerComponent,
  ButtonLoadingDirective,
} from '@recruitee/user-interface';
import { MaxFileSize, validateMaxFileSize, getHumanReadableFileSize } from '@recruitee/utils';

@Component({
  selector: 'rt-requisitions-approval-request-message',
  templateUrl: './approval-request-message.component.html',
  styleUrls: ['./approval-request-message.component.less'],
  standalone: true,
  imports: [
    AdminsAvatarsComponent,
    AttachmentItemComponent,
    BadgeComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    HeadingComponent,
    IconComponent,
    SpinnerComponent,
    TranslatePipe,
    ReactiveFormsModule,
    WysiwygComponent,
    ProsemirrorViewboxConfigDirective,
  ],
})
export class ApprovalRequestMessageComponent {
  public readonly trackById = trackByProp<{ id: number }>({ prop: 'id' });

  public wysiwygControl = new FormControl<EditorJSONState | object | null>(null);
  public ProsemirrorConfig = ProsemirrorViewboxConfig;

  @Input() public attachments: Attachment[] = [];
  @Input() public attachmentsUploading: string[] = [];
  @Input() public approvers: User[];
  @Input() public submitting: boolean;
  @Input() public message: object | null;

  @Output() public goBack = new EventEmitter<void>();
  @Output() public cancel = new EventEmitter<void>();
  @Output() public submit = new EventEmitter<ApprovalRequestPayload>();
  @Output()
  public uploadAttachmentClick: EventEmitter<File> = new EventEmitter();
  @Output()
  public deleteAttachmentClick: EventEmitter<number> = new EventEmitter();

  @ViewChild('fileInput', { static: true }) public fileInput: ElementRef<HTMLInputElement>;

  public actionButtons = [
    <EditorButton>{
      icon: 'attach-file',
      tooltipText: 'app.candidate.add_attachment',
      isActive: () => false,
      run: () => this.fileInput.nativeElement.click(),
    },
  ];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private notificationsController: NotificationsController,
  ) {}

  public ngOnInit() {
    this.wysiwygControl.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.cdRef.detectChanges();
    });
    if (this.message) {
      this.wysiwygControl.setValue(this.message);
    }
  }

  public submitRequest(): void {
    this.submit.emit({
      bodyJson: this.wysiwygControl.value,
      attachments: this.attachments.map(attachment => attachment.id),
    });
  }

  public uploadAttachment(files: FileList | null, fileInput: HTMLInputElement): void {
    if (files && files.length > 0) {
      const file = files[0];
      const validSize = validateMaxFileSize(file, MaxFileSize.File50MB);

      if (!validSize) {
        const message = this.i18n.translate('app.common.errors.file_size_limit_exceeded', {
          limit: getHumanReadableFileSize(MaxFileSize.File50MB),
          fileSize: getHumanReadableFileSize(file.size),
        });
        this.notificationsController.error(message);

        return;
      }
      this.uploadAttachmentClick.emit(file);
    }
    // @ts-ignore
    fileInput.value = null;
  }

  public deleteAttachment(attachment: Attachment): void {
    this.deleteAttachmentClick.emit(attachment.id);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByIndex(index: number, _: any): number {
    return index;
  }
}
